import { useEffect, useRef, useState } from "react";
import { probeCli } from "./auth.js";
import { AccountChip } from "./AccountChip.jsx";
import { MeetAvatar } from "./VideoTile.jsx";
import { isLiveRoom } from "./format.js";
import stageMark from "./icons/icon-a-stage.svg";

export function RoomList({
  rooms,
  loading,
  error,
  canCreate,
  joiningId,
  search,
  onSearch,
  filter,
  onFilter,
  signalling,
  onJoin,
  onUpdate,
  elapsedHint,
  identity,
  signingIn,
  authHint,
  openSignIn,
  onSignIn,
  onSignOut,
  canHost,
  onOpenAccount,
  onOpenHost,
  onOpenRoomLog,
}) {
  const [skipMic, setSkipMic] = useState({});
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [cli, setCli] = useState(null);
  const [editingId, setEditingId] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editIcon, setEditIcon] = useState("");
  const menuRef = useRef(null);

  useEffect(() => {
    if (openSignIn) setMenuOpen(true);
  }, [openSignIn]);

  useEffect(() => {
    if (!menuOpen) return;
    let cancelled = false;
    setCli(null);
    probeCli().then((p) => {
      if (!cancelled) setCli(p);
    });
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      cancelled = true;
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const pick = (source) => {
    setMenuOpen(false);
    onSignIn(source);
  };

  const q = search.trim().toLowerCase();
  const shown = rooms.filter((r) => {
    if (filter === "live" && !isLiveRoom(r)) return false;
    if (!q) return true;
    return (
      r.name.toLowerCase().includes(q) ||
      r._id.toLowerCase().includes(q) ||
      String(r.description || "").toLowerCase().includes(q) ||
      String(r.ownerName || "").toLowerCase().includes(q) ||
      String(r.owner || "").toLowerCase().includes(q)
    );
  });

  const copy = async (id) => {
    try {
      await navigator.clipboard.writeText(id);
      setCopied(id);
      setTimeout(() => setCopied(""), 900);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="bm-list">
      <div className="bm-list-head">
        <div>
          <div className="bm-title">
            <img className="bm-mark" src={stageMark} alt="" width="34" height="34" />
            <h1>Meetings</h1>
          </div>
          <p>Community calls and peer-to-peer rooms. Audio, video and screen share.</p>
        </div>
        <div className="bm-create-wrap">
          {identity ? (
            <AccountChip
              identity={identity}
              canHost={canHost}
              onOpenAccount={onOpenAccount}
              onOpenHost={onOpenHost}
              onSignOut={onSignOut}
            />
          ) : (
            <div className="bm-signmenu" ref={menuRef}>
              <button
                type="button"
                className="bm-signin"
                disabled={signingIn}
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((v) => !v)}
              >
                {signingIn ? "Signing in…" : "Sign in with Decent"}
              </button>
              {menuOpen && !signingIn ? (
                <div className="bm-signopts" role="menu">
                  <button type="button" className="bm-signopt" role="menuitem" onClick={() => pick("web")}>
                    app.beagle.chat
                    <small>Sign in with the web app</small>
                  </button>
                  <button
                    type="button"
                    className="bm-signopt"
                    role="menuitem"
                    onClick={() => pick("cli")}
                  >
                    CLI
                    <small>{cli?.status === "up" ? "localhost:8766 · on" : "localhost:8766"}</small>
                  </button>
                </div>
              ) : null}
            </div>
          )}
          {authHint ? <div className="bm-hostcap">{authHint}</div> : null}
        </div>
      </div>

      <div className="bm-filters">
        <div className="bm-search">
          <span className="bm-glyph" />
          <input value={search} onChange={(e) => onSearch(e.target.value)} placeholder="Search room name or ID" />
        </div>
        <button type="button" className={`bm-chip ${filter === "all" ? "on" : ""}`} onClick={() => onFilter("all")}>All</button>
        <button type="button" className={`bm-chip ${filter === "live" ? "on" : ""}`} onClick={() => onFilter("live")}>
          <span className="bm-dot" />Live
        </button>
      </div>

      {error ? <div className="bm-err">{error}</div> : null}

      <div className="bm-cards">
        {loading ? <div className="bm-empty">Loading rooms…</div> : null}
        {!loading && shown.length === 0 ? (
          <div className="bm-empty">
            <h3>No rooms yet</h3>
            <p>
              {identity
                ? "Rooms appear here once a host creates one. Create a room from Account."
                : "Sign in with Decent (app.beagle.chat or CLI), then Join."}
            </p>
          </div>
        ) : null}
        {shown.map((room) => {
          const live = isLiveRoom(room);
          const n = Number(room.participants || 0);
          return (
            <article key={room._id} className={`bm-card ${live ? "" : "idle"}`}>
              <div className="bm-card-main">
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  {room.icon ? (
                    <img className="bm-room-icon" src={room.icon} alt="" width="28" height="28" />
                  ) : null}
                  <div className="bm-name">{room.name}</div>
                  {room.engine === "agora" ? <span className="bm-tag">Agora</span> : null}
                  {room.requireMic === false ? <span className="bm-tag">mic optional</span> : null}
                  {live ? (
                    <span className="bm-live">
                      <i />LIVE {elapsedHint || ""}
                    </span>
                  ) : null}
                </div>
                <div className="bm-idrow">
                  <div className="bm-id">{room._id}</div>
                  <button type="button" className="bm-copy" onClick={() => copy(room._id)}>
                    {copied === room._id ? "copied" : "copy"}
                  </button>
                </div>
                {room.description ? <div className="bm-room-desc">{room.description}</div> : null}
                {room.ownerName || room.owner ? (
                  <div className="bm-room-owner">Owner {room.ownerName || room.owner}</div>
                ) : null}
                {room.isOwner || canCreate ? (
                  <div style={{ display: "flex", gap: 12 }}>
                    <button type="button" className="bm-host-link" onClick={() => onOpenRoomLog?.(room)}>
                      Room log
                    </button>
                    <button
                      type="button"
                      className="bm-host-link"
                      onClick={() => {
                        setEditingId(editingId === room._id ? "" : room._id);
                        setEditDescription(room.description || "");
                        setEditIcon(room.icon || "");
                      }}
                    >
                      {editingId === room._id ? "Cancel" : "Edit"}
                    </button>
                  </div>
                ) : null}
                {editingId === room._id && (room.isOwner || canCreate) ? (
                  <form
                    className="bm-create-extra"
                    onSubmit={async (e) => {
                      e.preventDefault();
                      if (busy) return;
                      setBusy(true);
                      try {
                        await onUpdate?.(room, {
                          description: editDescription.trim(),
                          icon: editIcon.trim(),
                          requireMic: room.requireMic !== false,
                        });
                        setEditingId("");
                      } finally {
                        setBusy(false);
                      }
                    }}
                  >
                    <div className="bm-search">
                      <input value={editIcon} onChange={(e) => setEditIcon(e.target.value)} placeholder="Icon URL (https)" />
                    </div>
                    <textarea
                      className="bm-desc-input"
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      placeholder="Description"
                      rows={3}
                    />
                    <button type="submit" className="bm-join sec" disabled={busy}>
                      {busy ? "Saving…" : "Save"}
                    </button>
                  </form>
                ) : null}
                <div className="bm-roster">
                  {n > 0 ? (
                    <>
                      <div className="bm-avs">
                        <MeetAvatar identity={{ name: room.name, userid: room._id }} size={26} />
                      </div>
                      <span>
                        {n} participant{n === 1 ? "" : "s"}
                      </span>
                    </>
                  ) : (
                    <span>0 participants · idle</span>
                  )}
                </div>
              </div>
              <div className="bm-join-col">
                {room.requireMic === false ? (
                  <label className="bm-check">
                    <input
                      type="checkbox"
                      checked={!!skipMic[room._id]}
                      onChange={(e) => setSkipMic((s) => ({ ...s, [room._id]: e.target.checked }))}
                    />
                    Join without mic
                  </label>
                ) : null}
                <button
                  type="button"
                  className={`bm-join ${live ? "" : "sec"}`}
                  disabled={!!joiningId}
                  onClick={() => {
                    if (!identity) {
                      setMenuOpen(true);
                      return;
                    }
                    onJoin({ ...room, _skipMic: !!skipMic[room._id] });
                  }}
                >
                  {joiningId === room._id ? "Joining…" : "Join"}
                </button>
              </div>
            </article>
          );
        })}
      </div>
      <div className="bm-foot">{rooms.length} rooms · signalling {signalling}</div>
    </div>
  );
}
